import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, Button, Card, Table, Spinner, Alert } from "react-bootstrap";
import Swal from "sweetalert2";
import api from "../../utils/axiosConfig";
import { fetchRegiones, fetchComunasByRegion } from "../../redux/regionComunaSlice";

// ---------- Utilidades ----------
const normalizeSpaces = (str = "") =>
  str.normalize("NFC").replace(/\s+/g, " ").trim();

const REGEX_NOMBRE_MODELO = /^[a-zA-Z]+$/; // (modelo) SIN tildes, SIN espacios
const REGEX_APELLIDOS_CTRL = /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]+$/; // (controlador) tildes + espacios

const isValidNombre = (s) => REGEX_NOMBRE_MODELO.test((s || "").trim());
const isValidApellido = (s) => !s || REGEX_APELLIDOS_CTRL.test(normalizeSpaces(s));

const isValidEmail = (s) =>
  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test((s || "").trim());

const isValidRut = (s) => /^[0-9]{1,8}$/.test((s || "").trim());
const isValidDv = (s) => /^[0-9K]$/.test((s || "").toUpperCase());
const isValidPhoneCL = (s) => /^\+56\d{8,9}$/.test((s || "").trim());

const DEFAULTS = {
  primer_apellido: "Apellido",
  segundo_apellido: "Apellido",
  telefono: "+56912345678",
  contacto_emergencia: "Contacto Emergencia",
  telefono_emergencia: "+56987654321",
  direccion: "Dirección no especificada",
  comuna_id: "341", // NO ESPECIFICADA
  region_id: "17", // Metropolitana
  rol: "socio",
  fecha_nacimiento: "1990-01-01",
  fecha_ingreso: new Date().toISOString().split("T")[0],
  empresa: "claro",
};

// Aplica defaults si están vacíos
const applyDefaultsIfEmpty = (data) => {
  const used = [];
  const next = { ...data };

  Object.entries(DEFAULTS).forEach(([k, v]) => {
    if (
      next[k] === undefined ||
      next[k] === null ||
      (typeof next[k] === "string" && next[k].trim() === "")
    ) {
      next[k] = v;
      used.push(k);
    }
  });

  return { next, used };
};

const sanitizeForSubmit = (raw, forceDefaultComuna) => {
  const d = { ...raw };

  d.nombre = (d.nombre || "").trim(); // SIN tildes/espacios, si tiene → error UI
  d.primer_apellido = normalizeSpaces(d.primer_apellido || "");
  d.segundo_apellido = normalizeSpaces(d.segundo_apellido || "");

  d.email = (d.email || "").trim().toLowerCase();
  d.email_personal = (d.email_personal || "").trim().toLowerCase() || null;

  d.rut = (d.rut || "").trim();
  d.dv = (d.dv || "").toUpperCase();

  d.telefono = (d.telefono || "").trim();
  d.telefono_emergencia = (d.telefono_emergencia || "").trim() || null;
  d.contacto_emergencia = normalizeSpaces(d.contacto_emergencia || "") || null;
  d.direccion = normalizeSpaces(d.direccion || "") || null;

  if (forceDefaultComuna) d.comuna_id = DEFAULTS.comuna_id;

  delete d.confirmEmail;
  return d;
};

// ---------- Componente ----------
const RegisterUser = () => {
  const dispatch = useDispatch();
  const { regiones = [], comunas = [], loading: geoLoading } = useSelector(
    (s) => s.regionComuna
  );
  const { rol: rolAuth } = useSelector((s) => s.auth?.usuario || {});
  const isAdmin = rolAuth === "administrador";

  useEffect(() => {
    dispatch(fetchRegiones());
  }, [dispatch]);

  const [formData, setFormData] = useState({
    email: "",
    confirmEmail: "",
    nombre: "",
    primer_apellido: DEFAULTS.primer_apellido,
    segundo_apellido: DEFAULTS.segundo_apellido,
    rut: "",
    dv: "",
    telefono: DEFAULTS.telefono,
    contacto_emergencia: DEFAULTS.contacto_emergencia,
    telefono_emergencia: DEFAULTS.telefono_emergencia,
    direccion: DEFAULTS.direccion,
    comuna_id: DEFAULTS.comuna_id,
    region_id: DEFAULTS.region_id,
    rol: DEFAULTS.rol,
    fecha_nacimiento: DEFAULTS.fecha_nacimiento,
    fecha_ingreso: DEFAULTS.fecha_ingreso,
    empresa: DEFAULTS.empresa,
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [emailMatch, setEmailMatch] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showDefaultsInfo, setShowDefaultsInfo] = useState(true);

  // -------- Handlers con validación inmediata --------
  const setErr = (name, msg) =>
    setFieldErrors((prev) => ({ ...prev, [name]: msg || "" }));

  const onNombre = (e) => {
    const v = e.target.value;
    setFormData((p) => ({ ...p, nombre: v }));
    // VALIDACIÓN EXACTA DEL MODELO
    if (!v.trim()) return setErr("nombre", "El nombre es obligatorio.");
    if (!isValidNombre(v))
      return setErr(
        "nombre",
        "El sistema de registro no admite tildes ni espacios en Nombre. Usa solo letras A-Z, sin espacios. Ej: 'Monica' (los compuestos van en apellidos)."
      );
    setErr("nombre", "");
  };

  const onApellido1 = (e) => {
    const v = normalizeSpaces(e.target.value);
    setFormData((p) => ({ ...p, primer_apellido: v }));
    if (!isValidApellido(v))
      return setErr(
        "primer_apellido",
        "Solo letras y espacios; se permiten tildes (áéíóúñü)."
      );
    setErr("primer_apellido", "");
  };

  const onApellido2 = (e) => {
    const v = normalizeSpaces(e.target.value);
    setFormData((p) => ({ ...p, segundo_apellido: v }));
    if (!isValidApellido(v))
      return setErr(
        "segundo_apellido",
        "Solo letras y espacios; se permiten tildes (áéíóúñü)."
      );
    setErr("segundo_apellido", "");
  };

  const onEmail = (e) => {
    const v = e.target.value;
    setFormData((p) => ({ ...p, email: v }));
    setEmailMatch(v === formData.confirmEmail);
    setErr("email", v && !isValidEmail(v) ? "El email no es válido." : "");
  };

  const onConfirmEmail = (e) => {
    const v = e.target.value;
    setFormData((p) => ({ ...p, confirmEmail: v }));
    setEmailMatch(v === formData.email);
    setErr("confirmEmail", v && v !== formData.email ? "Los correos no coinciden." : "");
  };

  const onRut = (e) => {
    const v = e.target.value.replace(/\D/g, "");
    setFormData((p) => ({ ...p, rut: v }));
    setErr("rut", v && !isValidRut(v) ? "El RUT debe tener entre 1 y 8 dígitos." : "");
  };

  const onDv = (e) => {
    const v = (e.target.value || "").slice(0, 1).toUpperCase();
    setFormData((p) => ({ ...p, dv: v }));
    setErr("dv", v && !isValidDv(v) ? "El dígito verificador debe ser 0-9 o K." : "");
  };

  const onTel = (e) => {
    const v = e.target.value.trim();
    setFormData((p) => ({ ...p, telefono: v }));
    setErr("telefono", v && !isValidPhoneCL(v) ? "Formato esperado: +569XXXXXXXX" : "");
  };

  const onRegion = (e) => {
    const regionId = e.target.value || "";
    setFormData((p) => ({ ...p, region_id: regionId, comuna_id: "" }));
    if (regionId) dispatch(fetchComunasByRegion(regionId));
  };

  // -------- Validación antes de enviar ----------
  const validateAll = () => {
    const errs = {};

    if (!formData.nombre.trim()) {
      errs.nombre = "El nombre es obligatorio.";
    } else if (!isValidNombre(formData.nombre)) {
      errs.nombre =
        "El sistema no admite tildes ni espacios en Nombre. Usa solo letras A-Z, sin espacios.";
    }

    if (!isValidApellido(formData.primer_apellido)) {
      errs.primer_apellido =
        "El primer apellido solo puede tener letras y espacios (se permiten tildes).";
    }
    if (!isValidApellido(formData.segundo_apellido)) {
      errs.segundo_apellido =
        "El segundo apellido solo puede tener letras y espacios (se permiten tildes).";
    }

    if (!isValidEmail(formData.email)) {
      errs.email = "El email no es válido.";
    }
    if (formData.confirmEmail !== formData.email) {
      errs.confirmEmail = "Los correos no coinciden.";
    }

    if (!isValidRut(formData.rut)) {
      errs.rut = "El RUT debe tener entre 1 y 8 dígitos.";
    }
    if (!isValidDv(formData.dv)) {
      errs.dv = "El dígito verificador debe ser 0-9 o K.";
    }

    if (formData.telefono && !isValidPhoneCL(formData.telefono)) {
      errs.telefono = "El teléfono debe ser un celular válido de Chile (+56...).";
    }

    if (showAdvanced) {
      if (!formData.region_id) errs.region_id = "Seleccione una región.";
      if (!formData.comuna_id) errs.comuna_id = "Seleccione una comuna.";
    }

    setFieldErrors(errs);
    return errs;
  };

  // -------- Mapear errores del backend ----------
  const mapServerError = (respData) => {
    const errs = {};
    let htmlMsg = "";

    if (respData?.code === "VALIDATION_ERROR" && Array.isArray(respData.details)) {
      // express-validator del controlador
      htmlMsg = respData.details.map((e) => `• ${e.msg}`).join("<br/>");
      respData.details.forEach((e) => {
        if (e.param) errs[e.param] = e.msg;
      });
    } else if (respData?.code) {
      // Códigos personalizados
      switch (respData.code) {
        case "DUPLICATE_RUT":
          errs.rut = "El RUT ya está registrado.";
          htmlMsg = "El RUT ya está registrado.";
          break;
        case "DUPLICATE_EMAIL":
          errs.email = "El email ya está registrado.";
          htmlMsg = "El email ya está registrado.";
          break;
        case "DUPLICATE_PERSONAL_EMAIL":
          errs.email_personal = "El correo personal ya está registrado.";
          htmlMsg = "El correo personal ya está registrado.";
          break;
        case "COMUNA_NOT_FOUND":
          errs.comuna_id = "La comuna especificada no existe.";
          htmlMsg = "La comuna especificada no existe.";
          break;
        default:
          htmlMsg = respData.error || "Error inesperado.";
          break;
      }
    } else if (respData?.error && /Validation.*nombre/i.test(respData.error)) {
      // fallback cuando viene el SequelizeValidationError en texto
      errs.nombre =
        "Nombre inválido según el modelo. Usa solo letras A-Z, sin tildes ni espacios.";
      htmlMsg =
        "El campo Nombre fue rechazado por el modelo (solo letras A-Z, sin tildes ni espacios).";
    } else if (respData?.error) {
      htmlMsg = respData.error;
    }

    return { errs, htmlMsg };
  };

  // -------- Enviar ----------
  const handleSubmit = async (e) => {
    e.preventDefault();

    const errs = validateAll();
    if (Object.keys(errs).length > 0) {
      Swal.fire({
        icon: "error",
        title: "Corrige los campos marcados",
        html: "Revisa los mensajes en rojo en el formulario.",
      });
      return;
    }

    // Aplico defaults a lo que esté vacío
    const { next: withDefaults, used } = applyDefaultsIfEmpty(formData);
    const payload = sanitizeForSubmit(withDefaults, !showAdvanced);

    const defaultsUsedList = used
      .map((k) => `• ${k.replaceAll("_", " ")}`)
      .join("<br/>");

    try {
      setSubmitting(true);
      const res = await api.post("/auth/register", payload);

      if (res.status === 201) {
        Swal.fire({
          icon: "success",
          title: "¡Usuario registrado!",
          html:
            `Se envió una contraseña temporal a <b>${payload.email}</b>.` +
            (used.length
              ? `<br/><br/><i>Se aplicaron valores por defecto en:</i><br/>${defaultsUsedList}`
              : ""),
        });

        // reset parcial
        setFormData((p) => ({
          ...p,
          email: "",
          confirmEmail: "",
          nombre: "",
          rut: "",
          dv: "",
        }));
        setFieldErrors({});
      }
    } catch (error) {
      console.error("Error al registrar usuario:", error);
      let resp = error?.response?.data;
      let { errs: mapped, htmlMsg } = mapServerError(resp);

      // Manejar SequelizeValidationError (nombre) explícitamente
      if (
        error?.response?.data?.errors?.[0]?.path === "nombre" ||
        /Validation.*nombre/i.test(error?.response?.data?.message || "")
      ) {
        mapped.nombre =
          mapped.nombre ||
          "Nombre inválido según el modelo. Usa solo letras A-Z, sin tildes ni espacios.";
        htmlMsg =
          htmlMsg ||
          "El campo Nombre fue rechazado por el modelo (solo letras A-Z, sin tildes ni espacios).";
      }

      setFieldErrors((p) => ({ ...p, ...mapped }));

      Swal.fire({
        icon: "error",
        title: "No se pudo registrar",
        html:
          htmlMsg ||
          "Ocurrió un error inesperado.<br/>Si el problema persiste, contacta al administrador.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container mt-4">
      <Card className="shadow">
        <Card.Header className="bg-primary text-white">
          <h2 className="mb-0">Registrar Nuevo Usuario</h2>
        </Card.Header>

        <Card.Body>
          {showDefaultsInfo && (
            <Alert
              variant="info"
              dismissible
              onClose={() => setShowDefaultsInfo(false)}
            >
              Si no completas ciertos campos, el sistema usará valores por defecto:
              <ul className="mb-0">
                <li>Teléfono: {DEFAULTS.telefono}</li>
                <li>Dirección: {DEFAULTS.direccion}</li>
                <li>Comuna: {DEFAULTS.comuna_id} (No especificada)</li>
                <li>Rol: {DEFAULTS.rol}</li>
                <li>Empresa: {DEFAULTS.empresa}</li>
                <li>Fechas por defecto si no indicas otras</li>
              </ul>
            </Alert>
          )}

          <Form onSubmit={handleSubmit} noValidate>
            <Table borderless className="mb-0">
              <tbody>
                {/* Nombre */}
                <tr>
                  <td width="50%">
                    <Form.Group controlId="formNombre" className="mb-3">
                      <Form.Label>Nombre *</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.nombre}
                        placeholder="Ej: Juan"
                        onChange={onNombre}
                        isInvalid={!!fieldErrors.nombre}
                        required
                      />
                      <Form.Control.Feedback type="invalid">
                        {fieldErrors.nombre}
                      </Form.Control.Feedback>
                      <Form.Text muted>
                        (Restricción actual del sistema: sin tildes ni espacios. Para nombres
                        compuestos, usa los apellidos.)
                      </Form.Text>
                    </Form.Group>
                  </td>
                  <td />
                </tr>

                {/* Email + Confirmación */}
                <tr>
                  <td width="50%">
                    <Form.Group controlId="formEmail" className="mb-3">
                      <Form.Label>Email *</Form.Label>
                      <Form.Control
                        type="email"
                        value={formData.email}
                        onChange={onEmail}
                        isInvalid={!!fieldErrors.email}
                        required
                      />
                      <Form.Control.Feedback type="invalid">
                        {fieldErrors.email}
                      </Form.Control.Feedback>
                      <Form.Text muted>
                        Se enviará una contraseña temporal a este correo.
                      </Form.Text>
                    </Form.Group>
                  </td>
                  <td width="50%">
                    <Form.Group controlId="formConfirmEmail" className="mb-3">
                      <Form.Label>Confirmar Email *</Form.Label>
                      <Form.Control
                        type="email"
                        value={formData.confirmEmail}
                        onChange={onConfirmEmail}
                        isInvalid={!emailMatch || !!fieldErrors.confirmEmail}
                        required
                      />
                      <Form.Control.Feedback type="invalid">
                        {fieldErrors.confirmEmail || "Los correos no coinciden."}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </td>
                </tr>

                {/* RUT + DV */}
                <tr>
                  <td width="50%">
                    <Form.Group controlId="formRut" className="mb-3">
                      <Form.Label>RUT (sin puntos ni guión) *</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.rut}
                        onChange={onRut}
                        isInvalid={!!fieldErrors.rut}
                        inputMode="numeric"
                        required
                      />
                      <Form.Control.Feedback type="invalid">
                        {fieldErrors.rut}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </td>
                  <td width="50%">
                    <Form.Group controlId="formDv" className="mb-3">
                      <Form.Label>DV *</Form.Label>
                      <Form.Control
                        type="text"
                        value={formData.dv}
                        onChange={onDv}
                        isInvalid={!!fieldErrors.dv}
                        maxLength={1}
                        required
                      />
                      <Form.Control.Feedback type="invalid">
                        {fieldErrors.dv}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </td>
                </tr>
              </tbody>
            </Table>

            {/* Toggle Avanzados */}
            <div className="text-center mb-3">
              <Button variant="link" onClick={() => setShowAdvanced(!showAdvanced)}>
                {showAdvanced ? "Ocultar campos avanzados" : "Mostrar campos avanzados"}
              </Button>
            </div>

            {showAdvanced && (
              <Table borderless className="mb-0">
                <tbody>
                  {/* Apellidos */}
                  <tr>
                    <td width="50%">
                      <Form.Group controlId="formPrimerApellido" className="mb-3">
                        <Form.Label>Primer Apellido</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.primer_apellido}
                          onChange={onApellido1}
                          isInvalid={!!fieldErrors.primer_apellido}
                        />
                        <Form.Control.Feedback type="invalid">
                          {fieldErrors.primer_apellido}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </td>
                    <td width="50%">
                      <Form.Group controlId="formSegundoApellido" className="mb-3">
                        <Form.Label>Segundo Apellido</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.segundo_apellido}
                          onChange={onApellido2}
                          isInvalid={!!fieldErrors.segundo_apellido}
                        />
                        <Form.Control.Feedback type="invalid">
                          {fieldErrors.segundo_apellido}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </td>
                  </tr>

                  {/* Teléfonos / contacto / dirección */}
                  <tr>
                    <td>
                      <Form.Group controlId="formTelefono" className="mb-3">
                        <Form.Label>Teléfono</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.telefono}
                          onChange={onTel}
                          isInvalid={!!fieldErrors.telefono}
                        />
                        <Form.Control.Feedback type="invalid">
                          {fieldErrors.telefono}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </td>
                    <td>
                      <Form.Group controlId="formContactoEmergencia" className="mb-3">
                        <Form.Label>Contacto de Emergencia</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.contacto_emergencia}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              contacto_emergencia: normalizeSpaces(e.target.value),
                            }))
                          }
                        />
                      </Form.Group>
                    </td>
                  </tr>

                  <tr>
                    <td>
                      <Form.Group controlId="formTelefonoEmergencia" className="mb-3">
                        <Form.Label>Teléfono de Emergencia</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.telefono_emergencia}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              telefono_emergencia: e.target.value.trim(),
                            }))
                          }
                        />
                      </Form.Group>
                    </td>
                    <td>
                      <Form.Group controlId="formDireccion" className="mb-3">
                        <Form.Label>Dirección</Form.Label>
                        <Form.Control
                          type="text"
                          value={formData.direccion}
                          onChange={(e) =>
                            setFormData((p) => ({
                              ...p,
                              direccion: normalizeSpaces(e.target.value),
                            }))
                          }
                        />
                      </Form.Group>
                    </td>
                  </tr>

                  {/* Región / Comuna */}
                  <tr>
                    <td>
                      <Form.Group controlId="formRegion" className="mb-3">
                        <Form.Label>Región</Form.Label>
                        <Form.Select
                          value={formData.region_id}
                          onChange={onRegion}
                          isInvalid={!!fieldErrors.region_id}
                        >
                          <option value="">Seleccione región...</option>
                          {regiones.map((r) => (
                            <option key={r.id} value={r.id}>
                              {r.nombre}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {fieldErrors.region_id}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </td>
                    <td>
                      <Form.Group controlId="formComuna" className="mb-3">
                        <Form.Label>Comuna</Form.Label>
                        <Form.Select
                          value={formData.comuna_id}
                          onChange={(e) =>
                            setFormData((p) => ({ ...p, comuna_id: e.target.value }))
                          }
                          isInvalid={!!fieldErrors.comuna_id}
                          disabled={geoLoading || !formData.region_id}
                        >
                          <option value="">
                            {geoLoading ? "Cargando..." : "Seleccione comuna..."}
                          </option>
                          {comunas.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.nombre}
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {fieldErrors.comuna_id}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </td>
                  </tr>

                  {/* Empresa / Rol */}
                  <tr>
                    <td>
                      <Form.Group controlId="formEmpresa" className="mb-3">
                        <Form.Label>Empresa</Form.Label>
                        <Form.Select
                          value={formData.empresa}
                          onChange={(e) =>
                            setFormData((p) => ({ ...p, empresa: e.target.value }))
                          }
                          isInvalid={!!fieldErrors.empresa}
                        >
                          <option value="claro">claro</option>
                          <option value="vtr">vtr</option>
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {fieldErrors.empresa}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </td>
                    <td>
                      <Form.Group controlId="formRol" className="mb-3">
                        <Form.Label>Rol</Form.Label>
                        <Form.Select
                          value={formData.rol}
                          onChange={(e) =>
                            setFormData((p) => ({ ...p, rol: e.target.value }))
                          }
                          disabled={!isAdmin}
                          isInvalid={!!fieldErrors.rol}
                        >
                          <option value="socio">socio</option>
                          <option value="administrador">administrador</option>
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {fieldErrors.rol}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </td>
                  </tr>

                  {/* Fechas */}
                  <tr>
                    <td>
                      <Form.Group controlId="formFechaNacimiento" className="mb-3">
                        <Form.Label>Fecha de Nacimiento</Form.Label>
                        <Form.Control
                          type="date"
                          value={formData.fecha_nacimiento}
                          onChange={(e) =>
                            setFormData((p) => ({ ...p, fecha_nacimiento: e.target.value }))
                          }
                          isInvalid={!!fieldErrors.fecha_nacimiento}
                        />
                        <Form.Control.Feedback type="invalid">
                          {fieldErrors.fecha_nacimiento}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </td>
                    <td>
                      <Form.Group controlId="formFechaIngreso" className="mb-3">
                        <Form.Label>Fecha de Ingreso</Form.Label>
                        <Form.Control
                          type="date"
                          value={formData.fecha_ingreso}
                          onChange={(e) =>
                            setFormData((p) => ({ ...p, fecha_ingreso: e.target.value }))
                          }
                          isInvalid={!!fieldErrors.fecha_ingreso}
                        />
                        <Form.Control.Feedback type="invalid">
                          {fieldErrors.fecha_ingreso}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </td>
                  </tr>
                </tbody>
              </Table>
            )}

            <div className="d-flex gap-2 justify-content-end mt-3">
              <Button
                variant="secondary"
                type="button"
                onClick={() => {
                  setFormData((p) => ({
                    ...p,
                    email: "",
                    confirmEmail: "",
                    nombre: "",
                    rut: "",
                    dv: "",
                  }));
                  setFieldErrors({});
                }}
              >
                Limpiar
              </Button>

              <Button variant="primary" type="submit" disabled={submitting}>
                {submitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Registrando...
                  </>
                ) : (
                  "Registrar"
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default RegisterUser;
